import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generatePersonSchema, generateWebSiteSchema } from '../../utils/seo';

export default function HomePage() {
  // SEO Configuration
  useSEO({
    title: 'Emine Sevim Keleş - Yazar, Eğitmen & İlham Verici Konuşmacı',
    description: 'Emine Sevim Keleş, yüzlerce çevrimiçi ve yüz yüze kurs, atölye ve seminer vermiş köklü bir yazar ve deneyimli bir eğitmendir. Bilgi, farkındalık ve bilinçli yaşam yoluyla hayatları dönüştürüyorum.',
    keywords: 'Emine Sevim Keleş, yazar, eğitmen, konuşmacı, kuantum eğitimi, bilinçli yaşam',
    canonical: '/',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebSiteSchema(),
        generatePersonSchema()
      ]
    }
  });

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    eventType: '',
    message: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('eventType', formData.eventType);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4p06drm5ik2goc1epbg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', eventType: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/3d7ea30179c566048afe026cf967dfbd/5fe1f5fc5f9a9bdfba9376991f2072b1.svg" 
              alt="Emine Sevim Keleş Logo" 
              className="h-16 w-auto"
            />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#about" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-navy hover:text-gold' : 'text-white hover:text-gold'}`}>Hakkımda</a>
            <a href="#book" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-navy hover:text-gold' : 'text-white hover:text-gold'}`}>Yeni Kitap</a>
            <a href="#trainings" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-navy hover:text-gold' : 'text-white hover:text-gold'}`}>Eğitimler</a>
            <a href="#contact" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-navy hover:text-gold' : 'text-white hover:text-gold'}`}>İletişim</a>
          </div>
          <a 
            href="#contact" 
            className="hidden md:block px-6 py-2.5 bg-gold text-navy rounded-full text-sm font-medium hover:bg-navy hover:text-gold transition-all whitespace-nowrap cursor-pointer"
          >
            İletişime Geç
          </a>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              isScrolled ? 'text-navy hover:bg-gold/10' : 'text-white hover:bg-white/10'
            }`}
            aria-label="Menü"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
        
        {/* Mobile Menu */}
        <div className={`md:hidden absolute top-full left-0 right-0 bg-white shadow-lg transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-6 py-4 space-y-4">
            <a 
              href="#about" 
              onClick={closeMobileMenu}
              className="block text-navy hover:text-gold text-base font-medium py-2 transition-colors cursor-pointer"
            >
              Hakkımda
            </a>
            <a 
              href="#book" 
              onClick={closeMobileMenu}
              className="block text-navy hover:text-gold text-base font-medium py-2 transition-colors cursor-pointer"
            >
              Yeni Kitap
            </a>
            <a 
              href="#trainings" 
              onClick={closeMobileMenu}
              className="block text-navy hover:text-gold text-base font-medium py-2 transition-colors cursor-pointer"
            >
              Eğitimler
            </a>
            <a 
              href="#contact" 
              onClick={closeMobileMenu}
              className="block text-navy hover:text-gold text-base font-medium py-2 transition-colors cursor-pointer"
            >
              İletişim
            </a>
            <a 
              href="#contact" 
              onClick={closeMobileMenu}
              className="block w-full px-6 py-3 bg-gold text-navy rounded-full text-base font-medium hover:bg-navy hover:text-gold transition-all text-center whitespace-nowrap cursor-pointer mt-4"
            >
              İletişime Geç
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=cinematic%20spiritual%20rebirth%20scene%20with%20small%20glowing%20golden%20sprout%20emerging%20from%20cracked%20dark%20soil%20surrounded%20by%20floating%20golden%20light%20particles%20rising%20upward%20smooth%20deep%20purple%20to%20gold%20gradient%20background%20symbolizing%20transformation%20and%20enlightenment%20with%20gentle%20warm%20light%20rays%20mystical%20atmosphere%20premium%20minimalistic%20elegant%20high%20resolution%20soft%20focus%20background%20sharp%20foreground%20inspirational%20hopeful%20spiritual%20awakening&width=1920&height=1080&seq=hero-spiritual-rebirth-001&orientation=landscape"
            alt="Hero Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-gold bg-white/10 backdrop-blur-sm mb-8">
              <div className="w-2 h-2 bg-gold rounded-full"></div>
              <span className="text-white text-sm font-medium">Yazar • Bilinçaltı Eğitmeni</span>
            </div>
            
            <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl text-white mb-6 leading-tight">
              Emine Sevim Keleş
            </h1>
            <p className="font-serif text-3xl md:text-4xl text-white mb-4">
              Kuantum Eğitimleri ile
            </p>
            <p className="font-serif text-3xl md:text-4xl text-gold italic mb-8">
              Zihninizi Özgürleştirin
            </p>
            
            <p className="text-lg md:text-xl text-white/90 mb-12 max-w-2xl mx-auto leading-relaxed">
              Zihin çoğu zaman gerçeklerin üzerini örter ve duru bir bakış açısı ile görmeye engel olur. Gerçek özgürlük, zihinden özgürleşmektir.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="#book" 
                className="px-8 py-4 bg-navy text-white rounded-full text-base font-medium hover:bg-gold hover:text-navy transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Yeni Kitabımı Keşfet
                <i className="ri-arrow-right-line"></i>
              </a>
              <a 
                href="#trainings" 
                className="px-8 py-4 bg-transparent text-white border-2 border-white rounded-full text-base font-medium hover:bg-white hover:text-navy transition-all whitespace-nowrap cursor-pointer"
              >
                Eğitimlerime Katıl
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-gold/10 to-transparent rounded-[32px]"></div>
              <div className="relative rounded-[32px] overflow-hidden shadow-xl border-l-2 border-t-2 border-gold">
                <img 
                  src="https://static.readdy.ai/image/3d7ea30179c566048afe026cf967dfbd/28b7ca544af5bbcfe093e40687690c13.png"
                  alt="Emine Sevim Keleş"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            
            <div>
              <div className="text-gold text-sm font-semibold tracking-widest uppercase mb-4">
                Yazar Hakkında
              </div>
              <h2 className="font-serif text-5xl md:text-6xl text-navy mb-8 leading-tight">
                Bilinçli Yaşam<br />Yoluyla Bilgelik
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Emine Sevim Keleş, yüzlerce çevrimiçi ve yüz yüze kurs, atölye ve seminer vermiş köklü bir yazar ve deneyimli bir eğitmendir. Bilgi, farkındalık ve bilinçli yaşam yoluyla hayatları dönüştürüyorum.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-12">
                Bireyleri güçlendirmeye derin bir bağlılıkla, kalıcı değişime ilham veren dönüştürücü öğrenme deneyimleri yaratmak için kadim bilgeliği modern metodolojilerle birleştiriyor.
              </p>
              
              <div className="grid grid-cols-3 gap-6">
                <div className="bg-cream rounded-2xl p-6 text-center shadow-sm">
                  <div className="font-serif text-4xl text-navy mb-2">100+</div>
                  <div className="text-sm text-gray-600">Canlı Seminer</div>
                </div>
                <div className="bg-cream rounded-2xl p-6 text-center shadow-sm">
                  <div className="font-serif text-4xl text-navy mb-2">500+</div>
                  <div className="text-sm text-gray-600">Online Eğitim</div>
                </div>
                <div className="bg-cream rounded-2xl p-6 text-center shadow-sm">
                  <div className="font-serif text-4xl text-navy mb-2">1000+</div>
                  <div className="text-sm text-gray-600">Dönüşen Hayat</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* New Book Launch Section */}
      <section id="book" className="py-24 bg-cream relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-5">
          <svg viewBox="0 0 200 200" className="w-full h-full">
            <path d="M 0,100 Q 50,50 100,100 T 200,100" stroke="#D4AF37" strokeWidth="2" fill="none" />
          </svg>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="relative transform hover:scale-105 transition-transform duration-500">
                <div className="absolute inset-0 bg-gold/20 blur-3xl rounded-full"></div>
                <img 
                  src="https://static.readdy.ai/image/3d7ea30179c566048afe026cf967dfbd/c50e2a764d63eb2d964368f84b7b134a.png"
                  alt="90 Günde Yükseliş"
                  className="relative w-full max-w-md mx-auto rounded-2xl shadow-2xl"
                />
              </div>
            </div>
            
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold text-white text-sm font-semibold mb-6 animate-pulse">
                YENİ ÇIKIŞ
              </div>
              
              <h2 className="font-serif text-5xl md:text-6xl text-navy mb-6 leading-tight">
                90 Günde Yükseliş
              </h2>
              
              <p className="font-serif text-2xl text-gray-600 italic mb-8">
                Kendini keşfetmeye dönüştürücü bir rehber
              </p>
              
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Sizi bilinçli yaşama doğru dönüştürücü bir yolculukta yönlendirecek derin bilgeliği ve pratik teknikleri keşfedin. Bu kitap, gerçek potansiyelinizi açığa çıkarmanıza ve hayatınızda kalıcı pozitif değişim yaratmanıza yardımcı olmak için kadim ruhani ilkeleri modern içgörülerle birleştiriyor.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://globalakademi.online/urun/90-gunde-yukselis-kitap/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-gold text-navy rounded-full text-base font-semibold hover:bg-navy hover:text-gold transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  Şimdi Sipariş Ver
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a 
                  href="https://www.instagram.com/p/DRxRTI8DeeF/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-transparent text-navy border-2 border-navy rounded-full text-base font-semibold hover:bg-navy hover:text-white transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  Örnek Bölüm Oku
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trainings & Seminars Section */}
      <section id="trainings" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-gold text-sm font-semibold tracking-widest uppercase mb-4">
              Eğitim Programları
            </div>
            <h2 className="font-serif text-5xl md:text-6xl text-navy mb-6">
              Eğitimler & Seminerler
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
              Bilincini yükseltmek ve kişisel gelişim yolculuğunu güçlendirmek için tasarlanmış dönüştürücü eğitim programlarını keşfedin.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                title: 'Bilinçaltı Eğitimi',
                category: 'Bilinçaltı',
                description: 'Bilinçaltınızın gücünü keşfedin ve hayatınızı dönüştürmek için bu güçlü aracı kullanmayı öğrenin. Zihinsel engelleri aşın ve gerçek potansiyelinize ulaşın.',
                duration: '8 hafta',
                format: 'Online & Canlı',
                image: 'https://readdy.ai/api/search-image?query=abstract%20subconscious%20mind%20visualization%20with%20golden%20light%20particles%20flowing%20through%20neural%20pathways%20representing%20mental%20transformation%20and%20consciousness%20with%20deep%20blue%20and%20gold%20colors%20ethereal%20spiritual%20minimalist%20background&width=600&height=400&seq=training-subconscious-001&orientation=landscape'
              },
              {
                title: 'Celcelutiye Eğitimi',
                category: 'Ruhani Gelişim',
                description: 'Kadim bilgelik ve ruhani pratiklerle iç huzurunuzu bulun. Celcelutiye\'nin dönüştürücü gücünü keşfedin ve ruhsal yolculuğunuzu derinleştirin.',
                duration: '6 hafta',
                format: 'Hibrit',
                image: 'https://readdy.ai/api/search-image?query=spiritual%20sacred%20geometry%20with%20golden%20light%20forming%20mystical%20patterns%20in%20peaceful%20space%20representing%20ancient%20wisdom%20and%20spiritual%20awakening%20with%20warm%20colors%20and%20ethereal%20glow%20simple%20elegant%20background&width=600&height=400&seq=training-celcelutiye-001&orientation=landscape'
              },
              {
                title: 'Duygusal ve Bedensel Arınma',
                category: 'Şifa & Arınma',
                description: 'Duygusal ve bedensel blokajlardan arının. Kendinizi yenilemek ve daha sağlıklı, dengeli bir yaşam sürmek için güçlü arınma teknikleri öğrenin.',
                duration: '10 hafta',
                format: 'Yüz Yüze',
                image: 'https://readdy.ai/api/search-image?query=healing%20purification%20scene%20with%20person%20surrounded%20by%20soft%20golden%20cleansing%20light%20releasing%20negative%20energy%20peaceful%20transformation%20atmosphere%20with%20warm%20colors%20and%20ethereal%20spiritual%20energy%20simple%20serene%20background&width=600&height=400&seq=training-purification-001&orientation=landscape'
              }
            ].map((training, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group"
              >
                <div className="relative h-56 w-full overflow-hidden">
                  <img 
                    src={training.image}
                    alt={training.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gold transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                </div>
                <div className="p-8">
                  <div className="inline-block px-3 py-1 rounded-full bg-gold/10 text-navy text-xs font-semibold mb-4">
                    {training.category}
                  </div>
                  <h3 className="font-semibold text-xl text-navy mb-3">
                    {training.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {training.description}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      <span>{training.duration}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-global-line"></i>
                      <span>{training.format}</span>
                    </div>
                  </div>
                  <a 
                    href="https://globalakademi.online/kuantum-egitimleri/" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold text-sm font-medium hover:underline flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  >
                    Daha Fazla Bilgi
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <a 
              href="https://globalakademi.online/kuantum-egitimleri/" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 border-2 border-navy text-navy rounded-full text-base font-semibold hover:bg-navy hover:text-white transition-all whitespace-nowrap cursor-pointer"
            >
              Tüm Eğitimleri Görüntüle
            </a>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-24 bg-gradient-to-br from-gold/5 to-cream">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-6xl mb-6">"</div>
            <p className="font-serif text-3xl md:text-4xl text-navy italic max-w-4xl mx-auto mb-6 leading-relaxed">
              Öğretileri bilinç anlayışımı derinden dönüştürdü ve hayal bile edemediğim olasılıklara kapılar açtı.
            </p>
            <p className="text-gray-600 text-sm">— Atölye Katılımcısı</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            {[
              { icon: 'ri-presentation-line', number: '100+', text: 'Canlı Seminer Verildi' },
              { icon: 'ri-video-line', number: '500+', text: 'Online Eğitim Gerçekleştirildi' },
              { icon: 'ri-group-line', number: '1000+', text: 'Öğrenci Etkilendi' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gold/10 flex items-center justify-center">
                  <i className={`${stat.icon} text-3xl text-navy`}></i>
                </div>
                <div className="font-serif text-5xl text-navy mb-3">{stat.number}</div>
                <div className="text-gray-600">{stat.text}</div>
              </div>
            ))}
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                rating: 5,
                text: 'Hayat değiştiren bir deneyim. Kuantum eğitimi gözlerimi bilincin yeni boyutlarına açtı.',
                author: 'Ayşe M.',
                role: 'Kuantum Eğitimi Mezunu'
              },
              {
                rating: 5,
                text: 'Emine\'nin bilgeliği ve rehberliği kişisel dönüşüm yolculuğumda çok önemli oldu.',
                author: 'Mehmet K.',
                role: 'Celcelutiye Eğitimi Katılımcısı'
              },
              {
                rating: 5,
                text: 'Bilinçaltı eğitimi tüm beklentilerimi aştı. Gerçekten dönüştürücü!',
                author: 'Zeynep R.',
                role: 'Bilinçaltı Eğitimi Mezunu'
              }
            ].map((review, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-md">
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-gold"></i>
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  "{review.text}"
                </p>
                <div>
                  <div className="font-semibold text-navy text-sm">{review.author}</div>
                  <div className="text-gray-500 text-xs">{review.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-instagram-line text-4xl text-gold"></i>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-navy mb-4">
              Yolculuğu Takip Edin
            </h2>
            <a 
              href="https://www.instagram.com/eminesevimkeles/" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold text-lg font-medium hover:underline cursor-pointer"
            >
              @eminesevimkeles
            </a>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              {
                title: "Potansiyelini Açığa Çıkar.",
                subtitle: "Kendini keşfetmek için doğru zamandasın.",
                image: "https://readdy.ai/api/search-image?query=serene%20person%20standing%20on%20mountain%20peak%20at%20sunrise%20with%20arms%20raised%20celebrating%20achievement%20surrounded%20by%20soft%20golden%20light%20rays%20representing%20unlocking%20potential%20and%20self-discovery%20peaceful%20inspiring%20atmosphere%20with%20warm%20colors%20elegant%20minimalist%20background&width=400&height=400&seq=insta-potential-002&orientation=squarish"
              },
              {
                title: "Fark Ettikçe Dönüşürsün.",
                subtitle: "Bilinç yükselir, yolun aydınlanır.",
                image: "https://readdy.ai/api/search-image?query=abstract%20transformation%20concept%20with%20golden%20butterfly%20emerging%20from%20cocoon%20surrounded%20by%20soft%20light%20representing%20personal%20growth%20and%20consciousness%20evolution%20elegant%20minimalist%20background&width=400&height=400&seq=insta-transformation-001&orientation=squarish"
              },
              {
                title: "Yaşadığın Sorunun Cevabı İçinde.",
                subtitle: "Dinlemeyi öğrendiğinde çözüm görünür.",
                image: "https://readdy.ai/api/search-image?query=serene%20nature%20scene%20with%20calm%20water%20reflecting%20golden%20sunset%20light%20symbolizing%20inner%20reflection%20and%20self-discovery%20peaceful%20atmosphere%20with%20warm%20colors%20simple%20elegant%20composition&width=400&height=400&seq=insta-reflection-001&orientation=squarish"
              },
              {
                title: "Gücün, Kendini Tanımakta Gizli.",
                subtitle: "İçindeki ışığa fırsat ver.",
                image: "https://readdy.ai/api/search-image?query=glowing%20golden%20light%20emanating%20from%20center%20representing%20inner%20strength%20and%20self-knowledge%20with%20soft%20rays%20spreading%20outward%20elegant%20spiritual%20minimalist%20background%20with%20warm%20tones&width=400&height=400&seq=insta-innerlight-001&orientation=squarish"
              }
            ].map((item, index) => (
              <a
                key={index}
                href="https://www.instagram.com/eminesevimkeles/"
                target="_blank"
                rel="noopener noreferrer"
                className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer"
              >
                <img 
                  src={item.image}
                  alt={`Instagram gönderisi ${index + 1}`}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-navy/80 to-navy/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center p-6 text-center">
                  <i className="ri-instagram-line text-white text-3xl mb-4"></i>
                  <p className="text-white font-serif text-lg font-semibold mb-2 leading-tight">
                    {item.title}
                  </p>
                  <p className="text-gold text-sm leading-relaxed">
                    {item.subtitle}
                  </p>
                </div>
              </a>
            ))}
          </div>
          
          {/* Instagram Embed Widget */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="bg-cream rounded-3xl p-8 text-center">
              <p className="text-navy text-base mb-6">
                Son gönderilerimi ve günlük ilham verici içerikleri görmek için Instagram'da beni takip edin
              </p>
              <div className="flex justify-center">
                <iframe
                  src="https://www.instagram.com/eminesevimkeles/embed"
                  width="400"
                  height="480"
                  frameBorder="0"
                  scrolling="no"
                  allowTransparency={true}
                  className="rounded-2xl shadow-lg"
                ></iframe>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <a 
              href="https://www.instagram.com/eminesevimkeles/" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-gold to-navy text-white rounded-full text-base font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-instagram-line text-xl"></i>
              Instagram'da Takip Et
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-navy relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-64 h-64 bg-gold rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-gold rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-2">
              <h2 className="font-serif text-4xl md:text-5xl text-white mb-6 leading-tight">
                Birlikte Anlamlı<br />
                Bir Şeyler<br />
                Yaratalım
              </h2>
              <p className="text-gray-300 text-base leading-relaxed mb-8">
                Konuşmalar, atölyeler ve seminerler için müsaitim. Kitleniz için nasıl dönüştürücü bir deneyim yaratabileceğimizi konuşalım.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-10 h-10 rounded-full border border-gold flex items-center justify-center">
                    <i className="ri-mail-line text-gold"></i>
                  </div>
                  <span className="text-sm">iletisim@eminesevimkeles.com</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-10 h-10 rounded-full border border-gold flex items-center justify-center">
                    <i className="ri-phone-line text-gold"></i>
                  </div>
                  <span className="text-sm">+90 530 526 48 96</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-10 h-10 rounded-full border border-gold flex items-center justify-center">
                    <i className="ri-map-pin-line text-gold"></i>
                  </div>
                  <span className="text-sm">İstanbul, Türkiye</span>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <a 
                  href="https://www.instagram.com/eminesevimkeles/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full border border-gold flex items-center justify-center text-gold hover:bg-gold hover:text-navy transition-all cursor-pointer"
                >
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a 
                  href="#" 
                  className="w-12 h-12 rounded-full border border-gold flex items-center justify-center text-gold hover:bg-gold hover:text-navy transition-all cursor-pointer"
                >
                  <i className="ri-linkedin-line text-xl"></i>
                </a>
                <a 
                  href="#" 
                  className="w-12 h-12 rounded-full border border-gold flex items-center justify-center text-gold hover:bg-gold hover:text-navy transition-all cursor-pointer"
                >
                  <i className="ri-youtube-line text-xl"></i>
                </a>
              </div>
            </div>
            
            <div className="md:col-span-3">
              <form 
                id="contact_form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white rounded-3xl p-8 md:p-10 shadow-2xl"
              >
                <div className="mb-6">
                  <label className="block text-gold text-xs font-semibold uppercase tracking-wider mb-2">
                    Adınız
                  </label>
                  <input 
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-gold outline-none text-navy transition-colors"
                    placeholder="Tam adınızı girin"
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-gold text-xs font-semibold uppercase tracking-wider mb-2">
                    E-posta Adresi
                  </label>
                  <input 
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-gold outline-none text-navy transition-colors"
                    placeholder="eposta@ornek.com"
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-gold text-xs font-semibold uppercase tracking-wider mb-2">
                    Etkinlik Türü
                  </label>
                  <select 
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-gold outline-none text-navy transition-colors cursor-pointer"
                  >
                    <option value="">Etkinlik türü seçin</option>
                    <option value="speaking">Konuşma Davetiyesi</option>
                    <option value="workshop">Atölye</option>
                    <option value="seminar">Seminar</option>
                    <option value="training">Eğitim Programı</option>
                    <option value="other">Diğer</option>
                  </select>
                </div>
                
                <div className="mb-6">
                  <label className="block text-gold text-xs font-semibold uppercase tracking-wider mb-2">
                    Mesaj
                  </label>
                  <textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-gold outline-none text-navy transition-colors resize-none"
                    placeholder="Etkinliğiniz veya sorunuz hakkında bana bilgi verin... (maksimum 500 karakter)"
                  ></textarea>
                  <div className="text-right text-xs text-gray-400 mt-1">
                    {formData.message.length}/500
                  </div>
                </div>
                
                {formStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                    Teşekkürler! Mesajınız başarıyla gönderildi.
                  </div>
                )}
                
                {formStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    Lütfen mesaj uzunluğunuzu kontrol edin ve tekrar deneyin.
                  </div>
                )}
                
                <button 
                  type="submit"
                  className="w-full py-4 bg-gold text-navy rounded-full text-base font-semibold hover:bg-navy hover:text-gold transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  Davetiye Gönder
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy rounded-t-[40px] pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-1">
              <h3 className="font-serif text-2xl text-white mb-3">
                Emine Sevim Keleş
              </h3>
              <p className="text-gray-400 italic text-sm mb-6">
                Bilinçli dönüşüme ilham vermek
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Keşfet</h4>
              <ul className="space-y-2">
                <li><a href="#about" className="text-gray-400 text-sm hover:text-gold hover:underline transition-colors cursor-pointer">Hakkımda</a></li>
                <li><a href="#book" className="text-gray-400 text-sm hover:text-gold hover:underline transition-colors cursor-pointer">Yeni Kitap</a></li>
                <li><a href="#trainings" className="text-gray-400 text-sm hover:text-gold hover:underline transition-colors cursor-pointer">Eğitimler</a></li>
                <li><a href="#contact" className="text-gray-400 text-sm hover:text-gold hover:underline transition-colors cursor-pointer">İletişim</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Öğren</h4>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="https://globalakademi.online/kuantum-egitimleri/" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 text-sm hover:text-gold hover:underline transition-colors cursor-pointer flex items-center gap-1"
                  >
                    Global Akademi Portalı
                    <i className="ri-external-link-line text-xs"></i>
                  </a>
                </li>
                <li><a href="#" className="text-gray-400 text-sm hover:text-gold hover:underline transition-colors cursor-pointer">Blog &amp; Makaleler</a></li>
                <li><a href="#" className="text-gray-400 text-sm hover:text-gold hover:underline transition-colors cursor-pointer">Ücretsiz Kaynaklar</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Bağlantıda Kalın</h4>
              <p className="text-gray-400 text-sm mb-4">
                Güncellemeler ve ilham almak için abone olun
              </p>
              <div className="flex gap-2">
                <input 
                  type="email"
                  placeholder="E-postanız"
                  className="flex-1 px-4 py-2 bg-navy/50 border border-gold/30 rounded-full text-white text-sm outline-none focus:border-gold transition-colors"
                />
                <button className="w-10 h-10 bg-gold rounded-full flex items-center justify-center text-navy hover:bg-white transition-colors cursor-pointer">
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gold/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © 2024 Emine Sevim Keleş. Tüm hakları saklıdır.
              </p>
              <div className="flex items-center gap-6">
                <a href="#" className="text-gray-400 text-sm hover:text-gold transition-colors cursor-pointer">Gizlilik Politikası</a>
                <a href="#" className="text-gray-400 text-sm hover:text-gold transition-colors cursor-pointer">Hizmet Şartları</a>
                <a href="https://fahribahsi.tr" target="_blank" rel="noopener noreferrer" className="text-gray-400 text-sm hover:text-gold transition-colors cursor-pointer">DevDesign</a>
              </div>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-0 right-0 font-serif text-[120px] text-navy/30 opacity-30 pointer-events-none select-none">
          ESK
        </div>
      </footer>
    </div>
  );
}
